"use client";

import { useState, type FormEvent } from "react";
import { SITE } from "@/lib/site";
import { Arrow, Check } from "./ModuleIcon";

// Main signup conversion (Google Ads). From the product's ads config.
const SIGNUP_CONVERSION = "AW-18199893944/6TQmCIKHrbYcELivseZD";

function utm() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out: Record<string, string> = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_adgroup", "gclid", "msclkid"].forEach((k) => {
    const v = p.get(k);
    if (v) out[k] = v;
  });
  return out;
}

function phIds() {
  if (typeof window === "undefined" || !window.posthog) return {};
  try {
    const ph = window.posthog as unknown as { get_distinct_id?: () => string; get_session_id?: () => string };
    return { ph_distinct_id: ph.get_distinct_id?.(), ph_session_id: ph.get_session_id?.() };
  } catch {
    return {};
  }
}

async function post(path: string, body: Record<string, unknown>) {
  try {
    await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  } catch {
    /* swallow — never block the UI */
  }
}

export function SignupForm({ variant = "full", id }: { variant?: "full" | "compact"; id?: string }) {
  const [done, setDone] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const mail = String(fd.get("email") || "").trim();
    setEmail(mail);
    const payload = {
      email: mail,
      source: SITE.source,
      intent: "early_access",
      role: fd.get("role") || "",
      company_size: fd.get("company_size") || "",
      landing_page: typeof window !== "undefined" ? window.location.pathname : "/",
      ...utm(),
      ...phIds(),
    };
    await post("/api/signup", payload);
    window.paConversion?.(SIGNUP_CONVERSION);
    window.paTrack?.("signup_submitted", { source: SITE.source, role: payload.role, company_size: payload.company_size });
    setDone(true);
    setSubmitting(false);
  }

  if (done) {
    return (
      <div id={id} className="rounded-xl border border-line bg-white p-5 shadow-soft">
        <div className="flex items-center gap-2.5 rounded-[8px] border border-action/25 bg-action-soft px-3.5 py-3 text-[14px] font-extrabold text-action-dark">
          <Check className="h-[18px] w-[18px]" /> You&apos;re on the early-access list. We&apos;ll be in touch soon.
        </div>
        {variant === "full" && <Survey email={email} />}
      </div>
    );
  }

  return (
    <div id={id} className={variant === "full" ? "rounded-xl border border-line bg-white/90 p-[18px] shadow-float" : ""}>
      <form onSubmit={onSubmit} className="grid gap-2.5" aria-label="Get early access to PropertyActions">
        <input
          type="email"
          name="email"
          required
          autoComplete="email"
          inputMode="email"
          placeholder="Work email"
          aria-label="Work email"
          className="min-h-[46px] w-full rounded-[8px] border border-[#cfd8df] bg-white px-3 py-3 text-[15px] outline-none focus:border-action focus:shadow-[0_0_0_3px_rgba(22,138,91,.12)]"
        />
        {variant === "full" && (
          <div className="grid gap-2.5 sm:grid-cols-2">
            <select name="role" defaultValue="" aria-label="Your role" className="min-h-[46px] w-full rounded-[8px] border border-[#cfd8df] bg-white px-3 py-3 text-[15px] text-slate outline-none focus:border-action">
              <option value="" disabled>Your role</option>
              <option value="block_manager">Block / Managing Agent</option>
              <option value="facilities_manager">Facilities Manager</option>
              <option value="responsible_person">Responsible Person / Dutyholder</option>
              <option value="consultant">Compliance Consultant</option>
              <option value="school_estates">School / Estates Team</option>
              <option value="other">Other</option>
            </select>
            <select name="company_size" defaultValue="" aria-label="Properties managed" className="min-h-[46px] w-full rounded-[8px] border border-[#cfd8df] bg-white px-3 py-3 text-[15px] text-slate outline-none focus:border-action">
              <option value="" disabled>Properties managed</option>
              <option value="1">1 property</option>
              <option value="2-10">2 to 10</option>
              <option value="11-50">11 to 50</option>
              <option value="50+">50+</option>
            </select>
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex min-h-[46px] w-full items-center justify-center gap-2 rounded-[8px] bg-action px-4 font-extrabold text-white shadow-[0_12px_24px_rgba(22,138,91,.22)] transition hover:bg-action-dark disabled:opacity-70"
        >
          {variant === "compact" ? "Join early access" : "Get early access"} <Arrow className="h-[17px] w-[17px]" />
        </button>
      </form>
      <p className="mt-2.5 text-center text-[13px] text-muted">Free first property during early access. No card required.</p>
    </div>
  );
}

// Two-step micro-survey shown after signup — feeds workflow + price validation.
function Survey({ email }: { email: string }) {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [method, setMethod] = useState("");
  const [tool, setTool] = useState("");
  const [price, setPrice] = useState("");

  if (step === 2) {
    return <p className="mt-3.5 text-[14px] font-extrabold text-action-dark">Thanks — that helps us prioritise the first workflow.</p>;
  }

  return (
    <div className="mt-3.5 rounded-[10px] border border-line bg-[#fbfcfb] p-3.5">
      {step === 0 && (
        <div>
          <div className="mb-2.5 font-extrabold text-ink">How do you track compliance actions today?</div>
          <Options
            name="method"
            value={method}
            onChange={setMethod}
            options={[
              ["spreadsheet", "Spreadsheet"],
              ["pdf_email", "PDFs and email reminders"],
              ["another_tool", "Another compliance tool"],
              ["consultant", "Consultant handles it"],
            ]}
          />
          {method === "another_tool" && (
            <input value={tool} onChange={(e) => setTool(e.target.value)} placeholder="Which tool?" className="mt-2.5 min-h-[42px] w-full rounded-[8px] border border-[#cfd8df] px-3 text-[14px] outline-none focus:border-action" />
          )}
          <SurveyNext label="Next" disabled={!method} onClick={() => setStep(1)} />
        </div>
      )}
      {step === 1 && (
        <div>
          <div className="mb-2.5 font-extrabold text-ink">What would this be worth per property per month?</div>
          <Options
            name="price"
            value={price}
            onChange={setPrice}
            options={[
              ["under_15", "Under £15"],
              ["15_35", "£15 to £35"],
              ["35_75", "£35 to £75"],
              ["not_sure", "Not sure yet"],
            ]}
          />
          <SurveyNext
            label="Submit"
            disabled={!price}
            onClick={() => {
              post("/api/signup/survey", { email, source: SITE.source, answer: method, freetext: tool, price_tolerance: price });
              window.paTrack?.("survey_completed", { method, price });
              setStep(2);
            }}
          />
        </div>
      )}
    </div>
  );
}

function Options({ name, value, onChange, options }: { name: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="grid gap-2">
      {options.map(([val, label]) => (
        <label
          key={val}
          className={`flex cursor-pointer items-center gap-2.5 rounded-[8px] border px-2.5 py-2.5 text-[14px] ${
            value === val ? "border-action/40 bg-action-soft text-action-dark" : "border-line bg-white text-slate"
          }`}
        >
          <span className={`h-3.5 w-3.5 rounded-full border-2 ${value === val ? "border-action border-4" : "border-[#aebbc5]"}`} />
          <input type="radio" name={name} value={val} checked={value === val} onChange={() => onChange(val)} className="hidden" />
          {label}
        </label>
      ))}
    </div>
  );
}

function SurveyNext({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} className="mt-2.5 inline-flex items-center gap-2 rounded-[8px] bg-ink px-3 py-2.5 text-[13px] font-extrabold text-white disabled:opacity-50">
      {label} <Arrow className="h-3.5 w-3.5" />
    </button>
  );
}
